import {flowRight as compose} from 'lodash';
import React, {useCallback, useState} from 'react';
import {Mutation} from 'react-apollo';
import {RouteComponentProps} from 'react-router';
import {Link} from 'react-router-dom';
import {
  Button,
  Checkbox,
  Container,
  Divider,
  Dropdown,
  Header,
  Input,
  Label,
  Segment,
} from 'semantic-ui-react';
import Alerts from '../components/Alerts';

import StoragePercentage from '../components/StoragePercentage';
import DangerModal from '../components/DangerModal';
import EditableImage from '../components/EditableImage';
import LegacyWBIcon from '../components/elements/LegacyWBIcon';
import {ErrorAlert} from '../components/ErrorPage';
import MemberAccess from '../components/MemberAccess';
import MemberInfo from '../components/MemberInfo';
import ProjectAccess from '../components/ProjectAccess';
import RateLimits from '../components/RateLimits';
import SlackIntegration from '../components/SlackIntegration';
import Loader from '../components/WandbLoader';
import '../css/Team.less';
import {
  ServerInfoComponent,
  UpdateEntityPhotoMutationFn,
  UserAccountType,
} from '../generated/graphql';
import {TEAM_PHOTO_UPDATE} from '../graphql/admin';
import {
  MemberData,
  TeamQueryResultProps,
  withTeamQuery,
} from '../graphql/teamQuery';
import {
  createInviteMutation,
  CreateInviteMutationFn,
  createServiceAccountMutation,
  CreateServiceAccountMutationFn,
  DELETE_INVITE,
  DeleteInviteMutationData,
  DeleteInviteMutationVariables,
  UPDATE_ENTITY,
  UPDATE_MEMBER,
  UpdateEntityMutationData,
  UpdateEntityMutationVariables,
  UpdateMemberMutationData,
  UpdateMemberMutationVariables,
} from '../graphql/users';
import config from '../config';
import {storagePage} from '../util/urls';
import {GetViewerQueryResponse, withViewer} from '../graphql/users_get_viewer';
import {viewerUsingAdminPrivileges} from '../util/admin';
import {prettyRegion} from '../util/cloudRegions';
import {extractErrorMessageFromApolloError} from '../util/errors';
import * as String from '../util/string';
import './Profile.css';
import makeComp from '../util/profiler';

interface TeamComponentProps {
  createInvite: CreateInviteMutationFn;
  createServiceAccount: CreateServiceAccountMutationFn;
}

type TeamProps = TeamComponentProps &
  TeamQueryResultProps &
  GetViewerQueryResponse;

const Team: React.FC<TeamProps & RouteComponentProps> = makeComp(
  ({viewer, teamQuery, createInvite, createServiceAccount, history}) => {
    const [accountType, setAccountType] = useState<UserAccountType | 'ADMIN'>(
      UserAccountType.User
    );
    const [emailOrUsername, setEmailOrUsername] = useState('');

    const sendInvite = useCallback(() => {
      if (!teamQuery.entity) {
        return;
      }
      if (accountType === UserAccountType.Service) {
        createServiceAccount({
          entityName: teamQuery.entity.name,
          description: emailOrUsername,
        }).then(teamQuery.refetch);
      } else {
        (String.isValidEmail(emailOrUsername)
          ? createInvite({
              entityName: teamQuery.entity.name,
              email: emailOrUsername,
              admin: accountType === 'ADMIN',
            })
          : createInvite({
              entityName: teamQuery.entity.name,
              username: emailOrUsername,
              admin: accountType === 'ADMIN',
            })
        ).then(teamQuery.refetch, e =>
          console.error(extractErrorMessageFromApolloError(e))
        );
      }
      setEmailOrUsername('');
    }, [
      emailOrUsername,
      accountType,
      createInvite,
      createServiceAccount,
      teamQuery.entity,
      teamQuery.refetch,
    ]);

    const getViewerMember = useCallback((): MemberData | null => {
      if (!teamQuery || !teamQuery.entity || !viewer) {
        return null;
      }
      const members = teamQuery.entity.members;
      return members.find(m => m.username === viewer.username) || null;
    }, [teamQuery, viewer]);

    const viewerMember = getViewerMember();

    const canDeleteMember = useCallback(
      (member: MemberData) => {
        if (viewer?.admin && viewerUsingAdminPrivileges()) {
          return true;
        }
        if (!viewerMember) {
          return false;
        }
        if (member.admin && !viewerMember.admin) {
          return false;
        }
        return true;
      },
      [viewer, viewerMember]
    );

    const isViewerMember = useCallback(
      (member: MemberData) => {
        if (!viewerMember) {
          return false;
        }
        return member.username === viewerMember.username;
      },
      [viewerMember]
    );

    if (teamQuery.loading) {
      return <Loader />;
    }

    if (teamQuery.entity.readOnly) {
      return (
        <ErrorAlert
          icon="stop circle outline"
          title="Cannot access team settings"
          message="You must be a member of this team to access its settings."
        />
      );
    }

    const nonServiceMemberCount = teamQuery.entity.members.filter(member => {
      return member.accountType !== UserAccountType.Service;
    }).length;

    return (
      <div className="settings-page">
        {teamQuery.entity && (
          <Container text>
            <Header as="h1">Team Settings for {teamQuery.entity.name}</Header>
            <Segment>
              <Header as="h3">Members ({nonServiceMemberCount})</Header>
              <div className="members-list">
                {teamQuery.entity.members.map((member, i) => (
                  <React.Fragment key={i}>
                    {i === 0 ? <></> : <Divider />}
                    <div className="member-list-item" key={member.email + i}>
                      <MemberInfo
                        member={member}
                        isViewer={isViewerMember(member)}
                      />
                      <div className="member-actions">
                        {member.pending && <Label horizontal>Invited</Label>}
                        {!member.pending && (
                          <Mutation<
                            UpdateMemberMutationData,
                            UpdateMemberMutationVariables
                          >
                            mutation={UPDATE_MEMBER}
                            onCompleted={teamQuery.refetch}>
                            {updateMember => (
                              <MemberAccess
                                member={member}
                                readOnly={
                                  !viewerMember?.admin &&
                                  !(
                                    viewer.admin && viewerUsingAdminPrivileges()
                                  )
                                }
                                setAdmin={(admin: boolean) =>
                                  updateMember({
                                    variables: {
                                      entityName: teamQuery.entity.name,
                                      user: member.id,
                                      admin,
                                    },
                                  })
                                }
                              />
                            )}
                          </Mutation>
                        )}
                        {canDeleteMember(member) && (
                          <Mutation<
                            DeleteInviteMutationData,
                            DeleteInviteMutationVariables
                          >
                            mutation={DELETE_INVITE}
                            onCompleted={teamQuery.refetch}>
                            {deleteInvite => (
                              <DangerModal
                                message={`Are you sure you want to remove ${member.username ||
                                  member.email ||
                                  member.name} from this team?`}
                                trigger={
                                  <LegacyWBIcon
                                    className="delete-action"
                                    name="delete"
                                  />
                                }
                                yes={() =>
                                  deleteInvite({
                                    variables: {
                                      id: member.id,
                                      entityName: teamQuery.entity.name,
                                    },
                                  })
                                }
                              />
                            )}
                          </Mutation>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              {teamQuery.entity.isTeam && (
                <div className="add-member-form">
                  <Input
                    fluid
                    data-test="add-team-member-input"
                    className="add-member-form--email"
                    placeholder={
                      ({
                        service: 'Enter a description for this service account',
                        user:
                          'Enter an email or username to invite a new member',
                        admin:
                          'Enter an email or username to invite a new admin',
                      } as {
                        [key: string]: string;
                      })[accountType]
                    }
                    value={emailOrUsername}
                    onChange={(_, {value}) => setEmailOrUsername(value.trim())}
                  />
                  <Dropdown
                    data-test="add-member-form-account-type"
                    className="add-member-form--account-type"
                    button
                    compact
                    options={[
                      {text: 'Member', value: UserAccountType.User},
                      {text: 'Admin', value: 'ADMIN'},
                      {text: 'Service', value: UserAccountType.Service},
                    ]}
                    value={accountType}
                    onChange={(_, {value}) =>
                      setAccountType(value as UserAccountType | 'ADMIN')
                    }
                  />
                  <Button
                    data-test="add-team-member-button"
                    onClick={sendInvite}>
                    {accountType === UserAccountType.Service
                      ? 'Create'
                      : 'Invite'}
                  </Button>
                </div>
              )}
            </Segment>
            <Segment>
              <Header as="h3">Team Avatar</Header>
              <Mutation mutation={TEAM_PHOTO_UPDATE}>
                {(teamUp: UpdateEntityPhotoMutationFn) => (
                  <EditableImage
                    photoUrl={teamQuery.entity.photoUrl}
                    displaySize={200}
                    label={false}
                    entityName={teamQuery.entity.name}
                    save={newVal => {
                      teamUp({
                        variables: {
                          photoUrl: newVal,
                          entityName: teamQuery.entity.name,
                        },
                      });
                    }}
                  />
                )}
              </Mutation>
            </Segment>
            <Segment>
              <Header as="h3">Team Alerts</Header>
              <p>Notify the team when a member's run finishes or crashes.</p>
              <Alerts
                entity={teamQuery.entity}
                entityRefetch={teamQuery.refetch}
                subscriptionTypes={['SlackChannelSubscription']}
              />
              <SlackIntegration
                history={history}
                entity={teamQuery.entity}
                entityRefetch={teamQuery.refetch}
                integrationReason={
                  'Configure a Slack integration so we can send your team alerts.'
                }
              />
            </Segment>
            <Mutation<UpdateEntityMutationData, UpdateEntityMutationVariables>
              mutation={UPDATE_ENTITY}
              onCompleted={teamQuery.refetch}>
              {updateEntity => (
                <>
                  <Segment>
                    <Header as="h3">Privacy</Header>
                    <div className="privacy-wrapper">
                      Default project privacy
                      <ProjectAccess
                        project={{
                          readOnly: teamQuery.entity.readOnlyAdmin,
                          access: teamQuery.entity.defaultAccess,
                          entity: {
                            readOnlyAdmin: true,
                            defaultAccess: teamQuery.entity.defaultAccess,
                            privateOnly: teamQuery.entity.privateOnly,
                            isTeam: teamQuery.entity.isTeam,
                          },
                        }}
                        updateProjectAccess={(access: string) =>
                          updateEntity({
                            variables: {
                              entityName: teamQuery.entity.name,
                              defaultAccess: access,
                            },
                          })
                        }
                      />
                    </div>
                    <Divider />
                    <div className="privacy-wrapper">
                      <Checkbox
                        toggle
                        label="Force all projects in this team to be private."
                        checked={teamQuery.entity.privateOnly}
                        disabled={teamQuery.entity.readOnlyAdmin}
                        onChange={(_, {checked}) =>
                          updateEntity({
                            variables: {
                              entityName: teamQuery.entity.name,
                              privateOnly: checked,
                            },
                          })
                        }
                      />
                    </div>
                    <Divider />
                    <div className="privacy-wrapper">
                      <Checkbox
                        toggle
                        label="Enable code saving by default"
                        checked={teamQuery.entity.codeSavingEnabled}
                        disabled={teamQuery.entity.readOnlyAdmin}
                        onChange={(_, {checked}) =>
                          updateEntity({
                            variables: {
                              entityName: teamQuery.entity.name,
                              codeSavingEnabled: checked,
                            },
                          })
                        }
                      />
                    </div>
                  </Segment>
                  <Segment>
                    <Header as="h3">Artifacts</Header>
                    <div className="defaults-wrapper">
                      Default cloud storage region to store the team's artifacts
                      <ServerInfoComponent>
                        {({loading, data}) => {
                          const regions =
                            data?.serverInfo?.availableCloudRegions ?? [];
                          const currentRegion =
                            teamQuery.entity.settings.defaultCloudRegion;

                          return (
                            <Dropdown
                              loading={loading}
                              options={regions.map(region => {
                                return {
                                  key: region.id,
                                  value: region.id,
                                  text: prettyRegion(region),
                                };
                              })}
                              trigger={`${prettyRegion(currentRegion)}`}
                              direction="left"
                              onChange={(_, {value}) => {
                                if (currentRegion.id !== value) {
                                  updateEntity({
                                    variables: {
                                      entityName: teamQuery.entity.name,
                                      settings: {
                                        defaultCloudRegionID: value as string,
                                      },
                                    },
                                  });
                                }
                              }}
                            />
                          );
                        }}
                      </ServerInfoComponent>
                    </div>
                  </Segment>
                </>
              )}
            </Mutation>
            <Segment>
              <Header as="h3">Storage</Header>
              <StoragePercentage
                available={config.MAX_BYTES}
                used={teamQuery.entity.storageBytes}
                selected={0}
              />
              <Link to={storagePage(teamQuery.entity.name)}>
                <Button style={{marginTop: 10}}>Manage Storage</Button>
              </Link>
            </Segment>
            {viewer != null && viewer.admin && viewerUsingAdminPrivileges() && (
              <RateLimits entity={teamQuery.entity} />
            )}
          </Container>
        )}
      </div>
    );
  },
  {id: 'Team', memo: true}
);

const withMutations = compose(
  createInviteMutation,
  createServiceAccountMutation
);

// export dumb component for testing purposes
export {Team};

export default withMutations(withViewer(withTeamQuery(Team)));
